import {
  Box,
  FormHelperText,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
} from "@mui/material";
import { orderBy } from "lodash-es";
import { useMemo } from "react";
import { List, type RowComponentProps } from "react-window";

import { BaseEntitySearchAutocompleteInheritableProps } from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { enumService } from "@/common/services/enum";
import {
  BaseEntityDto,
  EntityType,
  GeneralStrictEntityRelationInputDto,
} from "@/core/api/generated/v1";

import AppDropdownButton from "../../../Button/AppDropdownButton";
import { ApiEnumAutocompleteProps } from "../../../Enum/ApiEnumAutocomplete";
import InlineApiEnumValue from "../../../Enum/InlineApiEnumValue";
import AccessoryAutocomplete, {
  AccessoryAutocompleteProps,
} from "../../Accessory/AccessoryAutocomplete";
import AccessoryCheckAutocomplete from "../../AccessoryCheck/AccessoryCheckAutocomplete";
import AssetAutocomplete, { AssetAutocompleteProps } from "../../Asset/AssetAutocomplete";
import ChargeAutocomplete from "../../Charge/ChargeAutocomplete";
import DamageCostEvaluationAutocomplete from "../../DamageCostEvaluation/DamageCostEvaluationAutocomplete";
import DamageCostEvaluationAggregateAutocomplete from "../../DamageCostEvaluationAggregate/DamageCostEvaluationAggregateAutocomplete";
import DamageDetectionAutocomplete from "../../DamageDetection/DamageDetectionAutocomplete";
import DamageDetectionAggregateAutocomplete from "../../DamageDetectionAggregate/DamageDetectionAggregateAutocomplete";
import MaintenanceAutocomplete from "../../Maintenance/MaintenanceAutocomplete";
import RepairOperationAutocomplete from "../../RepairOperation/RepairOperationAutocomplete";
import SupplierAutocomplete from "../../Supplier/SupplierAutocomplete";
import VehicleAutocomplete, { VehicleAutocompleteProps } from "../../Vehicle/VehicleAutocomplete";
import WashAutocomplete from "../../Wash/WashAutocomplete";
import BaseEntityAutocompletePlaceholder from "../../components/BaseEntityAutocompletePlaceholder";

function RowComponent({
  entityTypes,
  handleEntityTypeChange,
  index,
  style,
}: RowComponentProps<{
  entityTypes: EntityType[];
  handleEntityTypeChange: (entityType: EntityType | null | undefined) => void;
}>) {
  const item = entityTypes[index];

  return (
    <MenuItem style={style} key={index} onClick={() => handleEntityTypeChange(item)}>
      <ListItemIcon>
        <AppIcon of='entity' />
      </ListItemIcon>
      <ListItemText>
        <InlineApiEnumValue type='EntityType' value={item} withDescription={false} />
      </ListItemText>
    </MenuItem>
  );
}

type BaseEntityAutocompletePropsLocal = BaseEntitySearchAutocompleteInheritableProps<BaseEntityDto>;

type EntityAutocompletePropsLocal =
  | Partial<VehicleAutocompleteProps>
  | Partial<AccessoryAutocompleteProps>
  | Partial<AssetAutocompleteProps>;

export interface EntityAggregatedAutocompleteProps {
  value: GeneralStrictEntityRelationInputDto | null | undefined;
  /** Entity types that can be selected. */
  onlyEntityTypes?: EntityType[];
  entityTypeAutocompleteProps?: Partial<ApiEnumAutocompleteProps<"EntityType">>;
  entityAutocompleteProps?: EntityAutocompletePropsLocal;
  disabled?: boolean;
  onChange?: (newValue: GeneralStrictEntityRelationInputDto | null | undefined) => void;
}

export default function GeneralStrictEntityRelationInput({
  value,
  onlyEntityTypes,
  entityTypeAutocompleteProps,
  entityAutocompleteProps,
  disabled,
  onChange,
}: EntityAggregatedAutocompleteProps) {
  const entityTypes = useMemo(
    () =>
      orderBy(
        enumService.getEnumValues("EntityType", { only: onlyEntityTypes, exceptNone: true }),
        (x) => x,
      ),
    [onlyEntityTypes],
  );

  const handleEntityTypeChange = (entityType: EntityType | null | undefined) => {
    onChange &&
      onChange(
        entityType
          ? { ...value, entityType: entityType || undefined, entityId: undefined }
          : undefined,
      );
  };

  const handleEntityIdChange = (entityId: string | null | undefined) => {
    onChange && onChange(value ? { ...value, entityId: entityId || undefined } : undefined);
  };

  const entityTypeInput = (
    <AppDropdownButton
      sx={{
        fontWeight: "inherit",
      }}
      size={entityAutocompleteProps?.size || "small"}
      disabled={
        entityTypeAutocompleteProps?.disabled || entityAutocompleteProps?.disabled || disabled
      }
      buttonProps={{
        color: "text",
        disableFocusRipple: true,
        disableElevation: true,
        disableRipple: true,
        disableTouchRipple: true,
      }}
      dropdownContent={
        <MenuList sx={{ width: { xxs: "80vw", md: 300 }, height: { xxs: "80vh", md: 250 } }}>
          {entityTypes.length <= 10 &&
            entityTypes.map((enumValue, i) => (
              <MenuItem
                key={i}
                onClick={() => {
                  handleEntityTypeChange(enumValue);
                }}
              >
                <ListItemIcon>
                  <AppIcon of='entity' />
                </ListItemIcon>
                <ListItemText>
                  <InlineApiEnumValue type='EntityType' value={enumValue} />
                </ListItemText>
              </MenuItem>
            ))}

          {entityTypes.length > 10 && (
            <List
              // height={250}
              // width={300}
              rowCount={entityTypes.length}
              rowHeight={32}
              overscanCount={10}
              rowComponent={RowComponent}
              rowProps={{ entityTypes, handleEntityTypeChange }}
            />
          )}
        </MenuList>
      }
    >
      {!value?.entityType && "Select entity type"}
      {value?.entityType && <InlineApiEnumValue type='EntityType' value={value?.entityType} />}
    </AppDropdownButton>
  );

  const entityAutocompletePropsComputed = useMemo<BaseEntityAutocompletePropsLocal>(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () => ({
      disabled: disabled || !value?.entityType,
      withCreate: false,
      ...entityAutocompleteProps,
      entityId: value?.entityId,
      textFieldProps: {
        ...entityAutocompleteProps?.textFieldProps,
        InputProps: {
          startAdornment: (
            <InputAdornment sx={{ mx: 0 }} position='start'>
              {entityTypeInput}
            </InputAdornment>
          ),
        },
      },
      onChange: (newValue) => {
        handleEntityIdChange(newValue?.id);
      },
    }),
    [entityAutocompleteProps, entityTypeInput],
  );

  return (
    <Box>
      {/* Select EntityType */}
      {!value?.entityType && (
        <BaseEntityAutocompletePlaceholder {...entityAutocompletePropsComputed} />
      )}

      {/* Select Entity */}
      {value?.entityType && (
        <>
          {renderIf()
            .if(value.entityType === EntityType.Accessory)
            .then(<AccessoryAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.Vehicle)
            .then(<VehicleAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.DamageDetection)
            .then(<DamageDetectionAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.DamageDetectionAggregate)
            .then(<DamageDetectionAggregateAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.DamageCostEvaluation)
            .then(<DamageCostEvaluationAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.DamageCostEvaluationAggregate)
            .then(
              <DamageCostEvaluationAggregateAutocomplete {...entityAutocompletePropsComputed} />,
            )
            .elseif(value.entityType === EntityType.AccessoryCheck)
            .then(<AccessoryCheckAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.RepairOperation)
            .then(<RepairOperationAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.WheelOperation)
            .then(<RepairOperationAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.Wash)
            .then(<WashAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.Maintenance)
            .then(<MaintenanceAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.Supplier)
            .then(<SupplierAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.Asset)
            .then(<AssetAutocomplete {...entityAutocompletePropsComputed} />)
            .elseif(value.entityType === EntityType.Charge)
            .then(<ChargeAutocomplete {...entityAutocompletePropsComputed} />)
            .else(<FormHelperText error>Unknown entity type {value.entityType}.</FormHelperText>)
            .render()}
        </>
      )}
    </Box>
  );
}
